import { SettingsIcon } from 'lucide-react';
import { FormikErrors } from 'formik';

import { RelativePathFieldset } from '@/react/portainer/gitops/RelativePathFieldset/RelativePathFieldset';
import { SourceId } from '@/react/portainer/gitops/sources/types';

import { Card } from '@@/primitives/Card';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';

import { ArtifactConfig, WorkflowEngine } from '../../types';
import { DeploymentType } from '../../detectDeploymentType';

import { DriftConfiguration } from './DriftConfiguration';

export function AdvancedConfiguration({
  values,
  setFieldValue,
  refName,
  sourceId,
  errors,
  deploymentType,
  isEditing,
  engine,
}: {
  values: ArtifactConfig;
  setFieldValue: <K extends keyof ArtifactConfig>(
    f: K,
    v: ArtifactConfig[K]
  ) => void;
  errors: FormikErrors<ArtifactConfig> | undefined;
  refName: string | undefined;
  sourceId: SourceId | undefined;
  deploymentType: DeploymentType;
  isEditing: boolean;
  engine: WorkflowEngine;
}) {
  return (
    <Card.Container>
      <Card.Header title="Advanced configurations" icon={SettingsIcon} />
      <Card.Body>
        {engine === 'reconcile' && (
          <DriftConfiguration
            value={values.driftMode}
            onChange={(driftMode) => setFieldValue('driftMode', driftMode)}
          />
        )}

        {deploymentType === DeploymentType.Kubernetes && (
          <>
            {/* An Edge stack has a namespace of its own, so which one wins is
                a real choice. A reconciled deployment has none: every resource
                goes to the namespace its own manifest declares, which is what
                the toggle's on position meant and what Flux does without one. */}
            {engine !== 'reconcile' && (
              <div className="form-group">
                <div className="col-sm-12">
                  <SwitchField
                    label="Use namespace(s) specified from manifest"
                    data-cy="use-manifest-namespaces-switch"
                    labelClass="col-sm-3 col-lg-2"
                    tooltip="If you have defined namespaces in your deployment file, turning this on will enforce the use of those only in the deployment."
                    checked={values.useManifestNamespaces}
                    onChange={(value) =>
                      setFieldValue('useManifestNamespaces', value)
                    }
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  label="Prune resources"
                  data-cy="kubernetes-prune-switch"
                  labelClass="col-sm-3 col-lg-2"
                  tooltip="When this artifact is updated, delete the resources it previously created that the manifest no longer declares. Turning this off leaves those resources running on the cluster with nothing in Portainer pointing at them."
                  checked={values.kubernetesPrune}
                  onChange={(value) => setFieldValue('kubernetesPrune', value)}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  label="Force recreate"
                  data-cy="kubernetes-force-apply-switch"
                  labelClass="col-sm-3 col-lg-2"
                  tooltip="Some fields cannot be changed after a resource is created. Turning this on lets Portainer delete and recreate such a resource instead of failing the deployment."
                  checked={values.kubernetesForceApply}
                  onChange={(value) =>
                    setFieldValue('kubernetesForceApply', value)
                  }
                />
              </div>
            </div>

            {values.kubernetesForceApply && (
              <div className="form-group">
                <div className="col-sm-12">
                  <TextTip color="orange">
                    Resources whose immutable fields changed will be deleted and
                    recreated. Expect downtime, and data loss for resources that
                    hold data.
                  </TextTip>
                </div>
              </div>
            )}
          </>
        )}

        {/* Relative path volumes and per-device configurations are Edge stack
            mechanics: the reconciliation engine applies what a path resolves to
            and has nowhere to bind a host path from. */}
        {engine !== 'reconcile' && (
          <RelativePathFieldset
            values={values.relativePath}
            onChange={(relativePath) =>
              setFieldValue('relativePath', relativePath)
            }
            gitModel={{ RepositoryReferenceName: refName, SourceId: sourceId }}
            errors={errors?.relativePath}
            isEditing={isEditing}
          />
        )}
      </Card.Body>
    </Card.Container>
  );
}
